import React, { useState, useEffect } from 'react';
import { ArrowLeftRight, Clock, CheckCircle, XCircle, Users, Calendar, AlertCircle, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const PedidosTrocaTab = ({ onAtualizarContador }) => {
  const [pedidos, setPedidos] = useState([]);
  const [pedidosPendentes, setPedidosPendentes] = useState([]);
  const [pedidosAguardandoConfirmacao, setPedidosAguardandoConfirmacao] = useState([]);
  const [pedidosComoColega, setPedidosComoColega] = useState([]); // Pedidos onde fui o colega envolvido
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [showNovoPedidoDialog, setShowNovoPedidoDialog] = useState(false);
  const [colaboradores, setColaboradores] = useState([]);
  
  const [novoPedido, setNovoPedido] = useState({
    // Turno Atual
    data: '',
    seccao: '',
    hora_inicio_atual: '',
    hora_fim_atual: '',
    // Turno Pretendido
    data_pretendida: '',
    seccao_pretendida: '',
    hora_inicio_pretendido: '',
    hora_fim_pretendido: '',
    // Colega
    colega_nome: '',
    colega_id: '',
    motivo: ''
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Carregar meus pedidos
      try {
        const pedidosResponse = await axios.get(`${API_URL}/api/menu-colaborador/pedidos-troca/meus`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPedidos(pedidosResponse.data || []);
      } catch (error) {
        console.error('Erro ao carregar meus pedidos:', error);
        setPedidos([]);
      }
      
      // Carregar pedidos pendentes (onde sou o colega envolvido)
      try {
        const pendentesResponse = await axios.get(`${API_URL}/api/menu-colaborador/pedidos-troca/pendentes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPedidosPendentes(pendentesResponse.data || []);
      } catch (error) {
        console.error('Erro ao carregar pedidos pendentes:', error);
        setPedidosPendentes([]);
      }
      
      // Carregar pedidos aguardando minha confirmação (como solicitante)
      try {
        const aguardandoResponse = await axios.get(`${API_URL}/api/menu-colaborador/pedidos-troca/aguardar-confirmacao`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPedidosAguardandoConfirmacao(aguardandoResponse.data || []);
      } catch (error) {
        console.error('Erro ao carregar pedidos aguardando confirmação:', error);
        setPedidosAguardandoConfirmacao([]);
      }
      
      // Carregar pedidos onde fui o colega envolvido (histórico completo)
      try {
        const comoColegaResponse = await axios.get(`${API_URL}/api/menu-colaborador/pedidos-troca/como-colega`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPedidosComoColega(comoColegaResponse.data || []);
      } catch (error) {
        console.error('Erro ao carregar pedidos como colega:', error);
        setPedidosComoColega([]);
      }
      
      // Carregar lista de colaboradores
      try {
        const colaboradoresResponse = await axios.get(`${API_URL}/api/menu-colaborador/colaboradores`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Colaboradores carregados:', colaboradoresResponse.data.length);
        setColaboradores(colaboradoresResponse.data);
      } catch (error) {
        console.error('Erro ao carregar colaboradores:', error);
        setColaboradores([]);
        toast.error('Erro ao carregar lista de colaboradores');
      }
      
    } catch (error) {
      console.error('Erro geral ao carregar dados:', error);
      setErro('Erro ao carregar dados. Por favor, tente novamente.');
      toast.error('Erro ao carregar alguns dados. Por favor, recarregue a página.');
    } finally {
      setLoading(false);
    }
  };

  const handleCriarPedido = async () => {
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user'));
      
      const pedidoData = {
        ...novoPedido,
        solicitante_id: user.id,
        solicitante_nome: user.nome
      };
      
      await axios.post(
        `${API_URL}/api/menu-colaborador/pedidos-troca`,
        pedidoData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Pedido de troca criado com sucesso!');
      setShowNovoPedidoDialog(false);
      setNovoPedido({
        data: '',
        seccao: '',
        hora_inicio_atual: '',
        hora_fim_atual: '',
        data_pretendida: '',
        seccao_pretendida: '',
        hora_inicio_pretendido: '',
        hora_fim_pretendido: '',
        colega_nome: '',
        colega_id: '',
        motivo: ''
      });
      carregarDados();
      if (onAtualizarContador) onAtualizarContador();
    } catch (error) {
      console.error('Erro ao criar pedido:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar pedido');
    }
  };

  const handleResponderPedido = async (pedidoId, aceita, observacao = '') => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_URL}/api/menu-colaborador/pedidos-troca/${pedidoId}/consentir`,
        { aceita, observacao_colega: observacao },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Pedido ${aceita ? 'aceite' : 'recusado'} com sucesso!`);
      carregarDados();
      if (onAtualizarContador) onAtualizarContador();
    } catch (error) {
      console.error('Erro ao responder pedido:', error);
      toast.error(error.response?.data?.detail || 'Erro ao responder pedido');
    }
  };

  const handleConfirmarTroca = async (pedidoId, confirma, observacao = '') => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_URL}/api/menu-colaborador/pedidos-troca/${pedidoId}/confirmar`,
        { confirma, observacao_solicitante: observacao },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Troca ${confirma ? 'confirmada! Aguardando aprovação da gestão' : 'cancelada'} com sucesso!`);
      carregarDados();
      if (onAtualizarContador) onAtualizarContador();
    } catch (error) {
      console.error('Erro ao confirmar troca:', error);
      toast.error(error.response?.data?.detail || 'Erro ao confirmar troca');
    }
  };

  const getEstadoBadge = (estado) => {
    const cores = {
      'Pendente de consentimento do colega': 'bg-yellow-100 text-yellow-800',
      'Aguardar confirmação do solicitante': 'bg-purple-100 text-purple-800',
      'Aguardar decisão da gestão': 'bg-blue-100 text-blue-800',
      'Aprovado': 'bg-green-100 text-green-800',
      'Rejeitado': 'bg-red-100 text-red-800',
      'Recusado pelo colega': 'bg-gray-100 text-gray-800',
      'Cancelado pelo solicitante': 'bg-gray-100 text-gray-800'
    };
    
    return (
      <Badge className={cores[estado] || 'bg-gray-100 text-gray-800'}>
        {estado}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">A carregar dados...</p>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="text-red-600 mb-4">❌ {erro}</div>
        <Button onClick={() => {
          setErro(null);
          carregarDados();
        }}>
          Tentar Novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Pedidos de Troca de Horário</h2>
          <p className="text-sm text-gray-600 mt-1">Gerir pedidos de troca de turno</p>
        </div>
        
        <Dialog open={showNovoPedidoDialog} onOpenChange={setShowNovoPedidoDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Novo Pedido
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Novo Pedido de Troca</DialogTitle>
              <DialogDescription>
                Preencha os detalhes do pedido de troca de horário
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="border-t pt-4 bg-blue-50 p-4 rounded-md">
                <h4 className="font-semibold mb-3 text-blue-900">Turno Atual</h4>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="space-y-2">
                    <Label>Data *</Label>
                    <Input
                      type="date"
                      value={novoPedido.data}
                      onChange={(e) => setNovoPedido({...novoPedido, data: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secção *</Label>
                    <Input
                      value={novoPedido.seccao}
                      onChange={(e) => setNovoPedido({...novoPedido, seccao: e.target.value})}
                      placeholder="Ex: Café"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hora Início *</Label>
                    <Input
                      type="time"
                      value={novoPedido.hora_inicio_atual}
                      onChange={(e) => setNovoPedido({...novoPedido, hora_inicio_atual: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hora Fim *</Label>
                    <Input
                      type="time"
                      value={novoPedido.hora_fim_atual}
                      onChange={(e) => setNovoPedido({...novoPedido, hora_fim_atual: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4 bg-green-50 p-4 rounded-md">
                <h4 className="font-semibold mb-3 text-green-900">Turno Pretendido</h4>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="space-y-2">
                    <Label>Data *</Label>
                    <Input
                      type="date"
                      value={novoPedido.data_pretendida}
                      onChange={(e) => setNovoPedido({...novoPedido, data_pretendida: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secção *</Label>
                    <Input
                      value={novoPedido.seccao_pretendida}
                      onChange={(e) => setNovoPedido({...novoPedido, seccao_pretendida: e.target.value})}
                      placeholder="Ex: Loja Avelal"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hora Início *</Label>
                    <Input
                      type="time"
                      value={novoPedido.hora_inicio_pretendido}
                      onChange={(e) => setNovoPedido({...novoPedido, hora_inicio_pretendido: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hora Fim *</Label>
                    <Input
                      type="time"
                      value={novoPedido.hora_fim_pretendido}
                      onChange={(e) => setNovoPedido({...novoPedido, hora_fim_pretendido: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Colega Envolvido *</Label>
                <Select
                  value={novoPedido.colega_id || undefined}
                  onValueChange={(value) => {
                    const colab = colaboradores.find(c => c.id === value);
                    setNovoPedido({
                      ...novoPedido,
                      colega_id: value,
                      colega_nome: colab?.nome || colab?.username || ''
                    });
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={colaboradores.length > 0 ? "Selecione o colega" : "A carregar colaboradores..."}>
                      {novoPedido.colega_id && colaboradores.find(c => c.id === novoPedido.colega_id) 
                        ? (colaboradores.find(c => c.id === novoPedido.colega_id)?.nome || 
                           colaboradores.find(c => c.id === novoPedido.colega_id)?.username)
                        : null
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {colaboradores.length === 0 ? (
                      <SelectItem value="none" disabled>Sem colaboradores disponíveis</SelectItem>
                    ) : (
                      colaboradores.map(colab => (
                        <SelectItem key={colab.id} value={colab.id}>
                          {colab.nome || colab.username}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Motivo (opcional)</Label>
                <Textarea
                  value={novoPedido.motivo}
                  onChange={(e) => setNovoPedido({...novoPedido, motivo: e.target.value})}
                  placeholder="Descreva o motivo do pedido..."
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowNovoPedidoDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCriarPedido}>
                Criar Pedido
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pedidos Pendentes de Resposta (onde sou o colega) */}
      {pedidosPendentes.length > 0 && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center text-yellow-800">
              <AlertCircle className="h-5 w-5 mr-2" />
              Pedidos Pendentes da Sua Resposta
            </CardTitle>
            <CardDescription>
              Você precisa dar consentimento a estes pedidos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {pedidosPendentes.map(pedido => (
              <Card key={pedido.id} className="bg-white">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg">{pedido.solicitante_nome}</h4>
                      <p className="text-sm text-gray-600">Pedido de troca de horário</p>
                    </div>
                    {getEstadoBadge(pedido.estado)}
                  </div>

                  {/* Registo de Quando Foi Feito o Pedido */}
                  {pedido.created_at && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-xs font-semibold text-blue-900 mb-1">📅 Pedido feito em:</p>
                      <p className="text-sm text-blue-800 font-medium">
                        {new Date(pedido.created_at).toLocaleDateString('pt-PT', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })} às {new Date(pedido.created_at).toLocaleTimeString('pt-PT', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="p-3 bg-gray-50 rounded-md">
                      <p className="text-xs text-gray-700 font-semibold mb-1">Turno Atual</p>
                      <p className="text-sm font-medium">{pedido.data} - {pedido.seccao}</p>
                      <p className="text-sm">{pedido.hora_inicio_atual} - {pedido.hora_fim_atual}</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-md">
                      <p className="text-xs text-blue-900 font-semibold mb-1">Turno Pretendido</p>
                      <p className="text-sm font-medium text-blue-900">{pedido.data_pretendida || pedido.data} - {pedido.seccao_pretendida || pedido.seccao}</p>
                      <p className="text-sm text-blue-900">{pedido.hora_inicio_pretendido} - {pedido.hora_fim_pretendido}</p>
                    </div>
                  </div>
                  {pedido.motivo && (
                    <p className="text-sm text-gray-700 mb-4 italic">Motivo: {pedido.motivo}</p>
                  )}
                  <div className="flex gap-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-green-600 text-green-600 hover:bg-green-50"
                      onClick={() => handleResponderPedido(pedido.id, true)}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Aceitar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
                      onClick={() => handleResponderPedido(pedido.id, false)}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Recusar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Pedidos Aguardando Minha Confirmação (como solicitante após colega aceitar) */}
      {pedidosAguardandoConfirmacao.length > 0 && (
        <Card className="border-purple-300 bg-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center text-purple-800">
              <Clock className="h-5 w-5 mr-2" />
              Pedidos Aguardando Sua Confirmação Final
            </CardTitle>
            <CardDescription>
              Seu colega aceitou a troca. Confirme para enviar à gestão.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {pedidosAguardandoConfirmacao.map(pedido => (
              <Card key={pedido.id} className="bg-white">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg">Troca com {pedido.colega_nome}</h4>
                      <p className="text-sm text-gray-600">Aguardando sua confirmação final</p>
                    </div>
                    {getEstadoBadge(pedido.estado)}
                  </div>

                  {/* Registo de Horas do Pedido */}
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="space-y-2">
                      {pedido.created_at && (
                        <div>
                          <p className="text-xs font-semibold text-green-900">📅 Pedido criado em:</p>
                          <p className="text-sm text-green-800 font-medium">
                            {new Date(pedido.created_at).toLocaleDateString('pt-PT', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })} às {new Date(pedido.created_at).toLocaleTimeString('pt-PT', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      )}
                      {pedido.data_resposta_colega && (
                        <div>
                          <p className="text-xs font-semibold text-green-900">✅ Aceite pelo colega em:</p>
                          <p className="text-sm text-green-800 font-medium">
                            {new Date(pedido.data_resposta_colega).toLocaleDateString('pt-PT', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })} às {new Date(pedido.data_resposta_colega).toLocaleTimeString('pt-PT', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="p-3 bg-gray-50 rounded-md">
                      <p className="text-xs text-gray-700 font-semibold mb-1">Turno Atual</p>
                      <p className="text-sm font-medium">{pedido.data} - {pedido.seccao}</p>
                      <p className="text-sm">{pedido.hora_inicio_atual} - {pedido.hora_fim_atual}</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-md">
                      <p className="text-xs text-purple-900 font-semibold mb-1">Turno Pretendido</p>
                      <p className="text-sm font-medium text-purple-900">{pedido.data_pretendida || pedido.data} - {pedido.seccao_pretendida || pedido.seccao}</p>
                      <p className="text-sm text-purple-900">{pedido.hora_inicio_pretendido} - {pedido.hora_fim_pretendido}</p>
                    </div>
                  </div>
                  {pedido.motivo && (
                    <p className="text-sm text-gray-700 mb-3 italic">Motivo: {pedido.motivo}</p>
                  )}
                  {pedido.observacao_colega && (
                    <div className="mb-4 p-3 bg-green-50 rounded-md border border-green-200">
                      <p className="text-xs font-semibold text-green-900">Resposta do Colega:</p>
                      <p className="text-sm text-green-800">{pedido.observacao_colega}</p>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <Button
                      size="sm"
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => handleConfirmarTroca(pedido.id, true)}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Confirmar Troca
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-gray-600 text-gray-600 hover:bg-gray-50"
                      onClick={() => handleConfirmarTroca(pedido.id, false)}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancelar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Meus Pedidos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <ArrowLeftRight className="h-5 w-5 mr-2" />
            Meus Pedidos
          </CardTitle>
          <CardDescription>Histórico dos pedidos de troca criados por si</CardDescription>
        </CardHeader>
        <CardContent>
          {pedidos.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Ainda não criou nenhum pedido de troca</p>
          ) : (
            <div className="space-y-4">
              {pedidos.map(pedido => (
                <Card key={pedido.id}>
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h4 className="font-semibold">Troca com {pedido.colega_nome}</h4>
                        <p className="text-sm text-gray-600">{pedido.seccao}</p>
                      </div>
                      {getEstadoBadge(pedido.estado)}
                    </div>

                    {/* Registo de Horas e Dias - Destacado */}
                    <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                      <h5 className="font-semibold text-blue-900 mb-3 flex items-center">
                        <Calendar className="h-4 w-4 mr-2" />
                        Registo de Horas e Dias
                      </h5>
                      <div className="space-y-2">
                        {pedido.created_at && (
                          <div className="flex items-start">
                            <span className="text-xs font-semibold text-blue-700 w-32">📝 Pedido criado:</span>
                            <span className="text-xs text-blue-900 font-medium">
                              {new Date(pedido.created_at).toLocaleDateString('pt-PT', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })} às {new Date(pedido.created_at).toLocaleTimeString('pt-PT', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        )}
                        {pedido.data_resposta_colega && (
                          <div className="flex items-start">
                            <span className={`text-xs font-semibold w-32 ${pedido.aceite_colega ? 'text-green-700' : 'text-red-700'}`}>
                              {pedido.aceite_colega ? '✅ Aceite em:' : '❌ Recusado em:'}
                            </span>
                            <span className={`text-xs font-medium ${pedido.aceite_colega ? 'text-green-900' : 'text-red-900'}`}>
                              {new Date(pedido.data_resposta_colega).toLocaleDateString('pt-PT', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })} às {new Date(pedido.data_resposta_colega).toLocaleTimeString('pt-PT', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        )}
                        {pedido.data_confirmacao_solicitante && (
                          <div className="flex items-start">
                            <span className={`text-xs font-semibold w-32 ${pedido.confirmado_solicitante ? 'text-purple-700' : 'text-gray-700'}`}>
                              {pedido.confirmado_solicitante ? '✅ Confirmei em:' : '❌ Cancelei em:'}
                            </span>
                            <span className={`text-xs font-medium ${pedido.confirmado_solicitante ? 'text-purple-900' : 'text-gray-900'}`}>
                              {new Date(pedido.data_confirmacao_solicitante).toLocaleDateString('pt-PT', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })} às {new Date(pedido.data_confirmacao_solicitante).toLocaleTimeString('pt-PT', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        )}
                        {pedido.data_decisao_gestao && (
                          <div className="flex items-start">
                            <span className={`text-xs font-semibold w-32 ${pedido.aprovado_gestao ? 'text-blue-700' : 'text-red-700'}`}>
                              {pedido.aprovado_gestao ? '✅ Aprovado em:' : '❌ Rejeitado em:'}
                            </span>
                            <span className={`text-xs font-medium ${pedido.aprovado_gestao ? 'text-blue-900' : 'text-red-900'}`}>
                              {new Date(pedido.data_decisao_gestao).toLocaleDateString('pt-PT', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })} às {new Date(pedido.data_decisao_gestao).toLocaleTimeString('pt-PT', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="p-3 bg-gray-50 rounded-md">
                        <p className="text-xs text-gray-700 font-semibold mb-1">Turno Atual</p>
                        <p className="font-medium">{pedido.data} - {pedido.seccao}</p>
                        <p>{pedido.hora_inicio_atual} - {pedido.hora_fim_atual}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-md">
                        <p className="text-xs text-gray-700 font-semibold mb-1">Turno Pretendido</p>
                        <p className="font-medium">{pedido.data_pretendida || pedido.data} - {pedido.seccao_pretendida || pedido.seccao}</p>
                        <p>{pedido.hora_inicio_pretendido} - {pedido.hora_fim_pretendido}</p>
                      </div>
                    </div>
                    {pedido.motivo && (
                      <p className="text-sm text-gray-700 mt-3 italic">Motivo: {pedido.motivo}</p>
                    )}
                    {pedido.observacao_colega && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <p className="text-xs font-semibold text-gray-700">Resposta do Colega:</p>
                        <p className="text-sm text-gray-700">{pedido.observacao_colega}</p>
                      </div>
                    )}
                    {pedido.observacao_solicitante && (
                      <div className="mt-3 p-3 bg-purple-50 rounded-md">
                        <p className="text-xs font-semibold text-purple-900">Minha Observação:</p>
                        <p className="text-sm text-purple-900">{pedido.observacao_solicitante}</p>
                      </div>
                    )}
                    {pedido.observacao_admin && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-md">
                        <p className="text-xs font-semibold text-blue-900">Decisão da Gestão:</p>
                        <p className="text-sm text-blue-900">{pedido.observacao_admin}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pedidos Onde Fui Envolvido (como colega) */}
      {pedidosComoColega.length > 0 && (
        <Card className="border-teal-300 bg-teal-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-teal-700" />
              Pedidos Onde Fui Envolvido
            </CardTitle>
            <CardDescription>
              Histórico de pedidos onde você foi o colega envolvido na troca
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pedidosComoColega.map(pedido => (
                <Card key={pedido.id} className="bg-white">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{pedido.solicitante_nome}</h4>
                        <p className="text-sm text-gray-600">Pediu troca de horário</p>
                      </div>
                      {getEstadoBadge(pedido.estado)}
                    </div>

                    {/* Timeline do pedido */}
                    <div className="space-y-2 mb-4 text-xs bg-gray-50 p-3 rounded-md">
                      {pedido.created_at && (
                        <div className="flex items-start">
                          <span className="text-gray-700 font-semibold w-32">📅 Pedido criado:</span>
                          <span className="text-gray-900 font-medium">
                            {new Date(pedido.created_at).toLocaleDateString('pt-PT', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })} às {new Date(pedido.created_at).toLocaleTimeString('pt-PT', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      )}
                      {pedido.data_resposta_colega && (
                        <div className="flex items-start">
                          <span className={`font-semibold w-32 ${pedido.aceite_colega ? 'text-green-700' : 'text-red-700'}`}>
                            {pedido.aceite_colega ? '✅ Você aceitou:' : '❌ Você recusou:'}
                          </span>
                          <span className={`font-medium ${pedido.aceite_colega ? 'text-green-900' : 'text-red-900'}`}>
                            {new Date(pedido.data_resposta_colega).toLocaleDateString('pt-PT', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })} às {new Date(pedido.data_resposta_colega).toLocaleTimeString('pt-PT', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      )}
                      {pedido.data_decisao_gestao && (
                        <div className="flex items-start">
                          <span className={`font-semibold w-32 ${pedido.aprovado_gestao ? 'text-blue-700' : 'text-red-700'}`}>
                            {pedido.aprovado_gestao ? '✅ Gestão aprovou:' : '❌ Gestão rejeitou:'}
                          </span>
                          <span className={`font-medium ${pedido.aprovado_gestao ? 'text-blue-900' : 'text-red-900'}`}>
                            {new Date(pedido.data_decisao_gestao).toLocaleDateString('pt-PT', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })} às {new Date(pedido.data_decisao_gestao).toLocaleTimeString('pt-PT', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Detalhes dos turnos */}
                    <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                      <div className="p-3 bg-gray-50 rounded-md">
                        <p className="text-xs text-gray-700 font-semibold mb-1">Turno Atual</p>
                        <p className="font-medium">{pedido.data} - {pedido.seccao}</p>
                        <p>{pedido.hora_inicio_atual} - {pedido.hora_fim_atual}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-md">
                        <p className="text-xs text-gray-700 font-semibold mb-1">Turno Pretendido</p>
                        <p className="font-medium">{pedido.data_pretendida || pedido.data} - {pedido.seccao_pretendida || pedido.seccao}</p>
                        <p>{pedido.hora_inicio_pretendido} - {pedido.hora_fim_pretendido}</p>
                      </div>
                    </div>

                    {pedido.motivo && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <p className="text-xs font-semibold text-gray-700">Motivo:</p>
                        <p className="text-sm text-gray-900">{pedido.motivo}</p>
                      </div>
                    )}

                    {pedido.observacao_admin && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-md">
                        <p className="text-xs font-semibold text-blue-900">Decisão da Gestão:</p>
                        <p className="text-sm text-blue-900">{pedido.observacao_admin}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PedidosTrocaTab;